import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import Select from 'react-select';
import axios from "axios";
import './EPFForm.css';
import RegistrationNavbar from "../../EmployeesProfile/PendingProfiles/EmployeeCreation/EmployeesRegistrationNavbar/EmployeeRegistartion/RegistrationNavbar";
import DatePicker from 'react-datepicker';
import { capitalizeFirstLetter } from "../../../utils/validations";

const EPFForm = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const employeeUniqueId = searchParams.get('employeeUniqueId') || location.state?.applicationId;
  //const employeeUniqueId = "4G-20250910661846";
  const [personalData, setPersonalData] = useState({
    id: "",
    surName: "",
    employeeName: "",
    guardianName: "",
    relationship: "",
    nameAsAadhar: "",
    gender: "",
    dateOfBirth: "",
    aadhaarNumber: "",
    contactNumber: "",
    emailAddress: "",
    photoUrl: "",
    photoFile: null,
    addressId: null, // Store the address ID from API
    addresses: {
      presentAddress: {
        line1: "",
        line2: "",
        block: "",
        district: "",
        state: "",
        country: "",
        postcode: ""
      },
      permanentAddress: {
        line1: "",
        line2: "",
        block: "",
        district: "",
        state: "",
        country: "",
        postcode: ""
      }
    },
    nomineeDetails: {
      nomineeName: "",
      relationship: "",
      stayingWithInsured: ""
    },
    nomineeAddress: "",
    qualification: ""
  });

  const [bankData, setBankData] = useState({
    id: null, // Store bankDetails ID from API
    accountHolderName: "",
    accountNumber: "",
    ifscCode: "",
    branchName: ""
  });

  const [errors, setErrors] = useState({
    surName: '',
    employeeName: '',
    guardianName: '',
    relationship: '',
    gender: '',
    dateOfBirth: '',
    aadhaarNumber: '',
    contactNumber: '',
    emailAddress: '',
    permanentAddress: '',
    presentAddress: '',
    nomineeName: '',
    nomineeRelationship: '',
    nomineeAddress: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    branchName: '',
    employeePhoto: '',
    qualification: ''
  });
  const [maritalStatuses, setMaritalStatuses] = useState([]);
  const [relationship, setRelationship] = useState([]);
  const [genders, setGenders] = useState([]);
  const [nomineeStaying, setNomineeStaying] = useState('');
  const [isQualFocused, setIsQualFocused] = useState(false);
  const [qualifications, setQualifications] = useState([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [nationalities, setNationalities] = useState([]);
  const [uanNumber, setUanNumber] = useState('');
  const [employeeCreationData, setEmployeeCreationData] = useState({ onBoardingDate: "" });
  const [isFocused, setIsFocused] = useState({
    gender: false,
    relationship: false,
    dateOfBirth: false,
    nomineeRelationship: false,
    nationality: false, // Already used in the form
    maritalStatus: false, // Already used in the form
    interviewDate: false // Add interviewDate
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Fetch personal info based on employeeUniqueId
  useEffect(() => {
    const fetchPersonalInfo = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/personal-info/${employeeUniqueId}`);
        const data = response.data;
        const address = data.addresses[0] || {};
        const employeeIDDetails = data.employeeIDDetails || {};
        const bank = data.bankDetails || {};

        setPersonalData(prev => ({
          ...prev,
          id: data.id || prev.id,
          surName: data.surname || prev.surName,
          employeeName: data.givenName || prev.employeeName,
          guardianName: data.guardianName || prev.guardianName,
          relationship: data.relationship?.id ? Number(data.relationship.id) : prev.relationship,
          nameAsAadhar: employeeIDDetails.nameAsPerAadhaar || prev.nameAsAadhar,
          gender: data.gender?.id ? Number(data.gender.id) : prev.gender,
          dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
          aadhaarNumber: employeeIDDetails.aadhaarNumber || prev.aadhaarNumber,
          panCardNumber: employeeIDDetails.panCardNumber || prev.panCardNumber,
          nameAsPerPanCard: employeeIDDetails.nameAsPerPanCard || prev.nameAsPerPanCard,
          contactNumber: data.contactNumber?.slice(0, 10) || prev.contactNumber,
          emailAddress: data.email || prev.emailAddress,
          photoUrl: employeeIDDetails.employeepassportSizeFileName || prev.photoUrl,
          photoFile: null,
          addressId: address.id || prev.addressId,
          addresses: {
            presentAddress: {
              line1: address.presentAddressLine1 || prev.addresses.presentAddress.line1,
              line2: address.presentAddressLine2 || prev.addresses.presentAddress.line2,
              block: address.presentBlock || prev.addresses.presentAddress.block,
              district: address.presentDistrict?.districtName || prev.addresses.presentAddress.district,
              state: address.presentState?.stateName || prev.addresses.presentAddress.state,
              country: address.presentCountry?.nationalityName || prev.addresses.presentAddress.country,
              postcode: address.presentPostCode || prev.addresses.presentAddress.postcode
            },
            permanentAddress: {
              line1: address.permanentAddressLine1 || prev.addresses.permanentAddress.line1,
              line2: address.permanentAddressLine2 || prev.addresses.permanentAddress.line2,
              block: address.permanentBlock || prev.addresses.permanentAddress.block,
              district: address.permanentDistrict?.districtName || prev.addresses.permanentAddress.district,
              state: address.permanentState?.stateName || prev.addresses.permanentAddress.state,
              country: address.permanentCountry?.nationalityName || prev.addresses.permanentAddress.country,
              postcode: address.permanentPostCode || prev.addresses.permanentAddress.postcode
            }
          },
          nomineeDetails: {
            nomineeName: employeeIDDetails.nomineeName || prev.nomineeDetails.nomineeName,
            relationship: employeeIDDetails.nomineeRelationship?.id ? Number(employeeIDDetails.nomineeRelationship.id) : prev.nomineeDetails.relationship,
            stayingWithInsured: employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no"
          },
          nomineeAddress: employeeIDDetails.nomineePresentAddress || prev.nomineeAddress,
          qualification: data.qualification?.id || prev.qualification
        }));

        setEmployeeCreationData(prev => ({
          ...prev,
          onBoardingDate: data.onBoardingDate || prev.onBoardingDate // Adjust based on API response
        }));

        setBankData({
          id: bank.id || null,
          accountHolderName: bank.accountHolderName || "",
          accountNumber: bank.accountNumber || "",
          ifscCode: bank.ifscCode || "",
          branchName: bank.branchName || ""
        });
        setNomineeStaying(employeeIDDetails.isNomineeStayingWithYou ? "yes" : "no");
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Error fetching personal info:', error);
      }
    };
    if (employeeUniqueId) {
      fetchPersonalInfo();
    }
  }, [employeeUniqueId]);


  useEffect(() => {
    const fetchNationalities = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/nationality`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setNationalities(data.map(nat => ({
            value: nat.id,
            label: nat.nationalityName,
          })));
          console.log('Nationalities:', data);
        } else {
          console.error('Invalid response format: nationalities should be an array');
        }
      } catch (error) {
        console.error('Error fetching nationalities:', error);
      }
    };
    fetchNationalities();
  }, []);


  // Fetch relationships
  useEffect(() => {
    const fetchRelationships = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/relationships`);
        const data = await response.json();
        if (Array.isArray(data)) {
          setRelationship(data.map(rel => ({
            value: rel.id,
            label: rel.relation
          })));
          console.log('Relationships:', data);
        } else {
          console.error('Invalid response format: relationships should be an array');
        }
      } catch (error) {
        console.error('Error fetching relationships:', error);
      }
    };
    fetchRelationships();
  }, []);
  useEffect(() => {
    const fetchMaritalStatuses = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/marital-status`);
        const data = response.data;
        if (Array.isArray(data)) {
          setMaritalStatuses(data.map(item => ({
            value: item.id,
            label: item.maritalStatus,
          })));
          console.log('Marital Statuses:', data);
        } else {
          console.error('Invalid response format: marital statuses should be an array');
        }
      } catch (error) {
        console.error('Error fetching marital statuses:', error);
      }
    };
    fetchMaritalStatuses();
  }, []);
  // Fetch genders
  useEffect(() => {
    const fetchGenders = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/genders`);
        const data = response.data;
        if (Array.isArray(data)) {
          setGenders(data.map(item => ({
            value: item.id,
            label: item.gender
          })));
          console.log('Genders:', data);
        } else {
          console.error('Invalid response format: genders should be an array');
        }
      } catch (error) {
        console.error('Error fetching genders:', error);
      }
    };
    fetchGenders();
  }, []);
  useEffect(() => {
    const fetchQualifications = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/qualifications`);
        const data = response.data;
        if (Array.isArray(data)) {
          const options = data.map((q) => ({
            value: q.id,
            label: q.qualificationName,
          }));
          setQualifications(options);
          console.log('Qualifications:', data);
        } else {
          console.error('Invalid response format: qualifications should be an array');
        }
      } catch (error) {
        console.error('Error fetching qualifications:', error);
      }
    };
    fetchQualifications();
  }, []);
  const formatAccountHolderName = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatBranchName = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const handleInputChange = (eOrOption, fieldName) => {
    if (eOrOption && eOrOption.target) {
      const { name, value } = eOrOption.target;
      if (name === "nomineeName") {
        setPersonalData(prev => ({
          ...prev,
          nomineeDetails: {
            ...prev.nomineeDetails,
            [name]: value
          }
        }));
        console.log('Updated nomineeDetails:', { ...personalData.nomineeDetails, [name]: value });
      } else if (name === "onBoardingDate") {
        setEmployeeCreationData(prev => ({
          ...prev,
          [name]: value
        }));
        console.log('Updated employeeCreationData:', { ...employeeCreationData, [name]: value });
      } else {
        setPersonalData(prev => ({
          ...prev,
          [name]: value
        }));
        console.log('Updated personalData:', { ...personalData, [name]: value });
      }
      if (name === "employeeName") {
        localStorage.setItem("employeeName", value);
      }
    } else {
      const value = eOrOption ? Number(eOrOption.value) : '';
      if (fieldName === "nomineeRelationship") {
        setPersonalData(prev => ({
          ...prev,
          nomineeDetails: {
            ...prev.nomineeDetails,
            relationship: value
          }
        }));
      } else {
        setPersonalData(prev => ({
          ...prev,
          [fieldName]: value
        }));
      }
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleAccountChange = (e) => {
    const { name, value } = e.target;
    setBankData(prev => ({
      ...prev,
      [name]: value
    }));
    let error = '';
    if (name === "accountNumber" && value && !/^\d{9,15}$/.test(value)) {
      error = "Account number must be 9 to 15 digits";
    }
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      alert("Please fill all required fields correctly before submitting.");
      return;
    }

    const fullName = localStorage.getItem("full_name");
    const role = localStorage.getItem("role");
    const employeeId = localStorage.getItem("employeeId");

    const formData = new FormData();
    if (personalData.photoFile) {
      formData.append('file', personalData.photoFile);
    }

    const payload = {
      nameAsPerAadhaar: personalData.nameAsAadhar,
      mobileNumber: personalData.contactNumber,
      genderId: personalData.gender,
      dateOfBirthAsPerAadhaar: personalData.dateOfBirth,
      fatherOrHusbandName: personalData.guardianName,
      relationshipId: personalData.relationship,
      addressDetailsId: personalData.addressId,
      nomineeName: personalData.nomineeDetails.nomineeName,
      nomineeRelationshipId: personalData.nomineeDetails.relationship,
      nomineePresentAddress: personalData.nomineeAddress,
      isNomineeStayingWithYou: personalData.nomineeDetails.stayingWithInsured === "yes",
      bankDetailsId: bankData.id,
      qualificationId: personalData.qualification,
      onBoardingDate: employeeCreationData.onBoardingDate, // Add onBoardingDate
      auditLog: {
        employeeId: employeeId,
        role: role,
        name: fullName
      }
    };

    formData.append('request', JSON.stringify(payload));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/employees/epf/${employeeUniqueId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('EPF creation successful:', response.data);
      alert('EPF form submitted successfully!');
      localStorage.removeItem('personalData');
      localStorage.removeItem('bankData');
      setPersonalData({
        id: "",
        surName: "",
        employeeName: "",
        guardianName: "",
        relationship: "",
        nameAsAadhar: "",
        gender: "",
        dateOfBirth: "",
        aadhaarNumber: "",
        contactNumber: "",
        emailAddress: "",
        photoUrl: "",
        photoFile: null,
        addressId: null,
        addresses: {
          presentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          },
          permanentAddress: {
            line1: "",
            line2: "",
            block: "",
            district: "",
            state: "",
            country: "",
            postcode: ""
          }
        },
        nomineeDetails: {
          nomineeName: "",
          relationship: "",
          stayingWithInsured: ""
        },
        nomineeAddress: "",
        qualification: ""
      });
      setBankData({
        id: null,
        accountHolderName: "",
        accountNumber: "",
        ifscCode: "",
        branchName: ""
      });
      setEmployeeCreationData({
        onBoardingDate: "" // Reset onBoardingDate
      });
      setQualifications([]);
      setUanNumber('');
    } catch (error) {
      console.error('Error submitting EPF form:', error);
      alert('Failed to submit EPF form. Please try again.');
    }
  };

  const currentYear = new Date().getFullYear();
  const minDate = new Date(1965, 0, 1).toISOString().split('T')[0];
  const today = new Date();
  const maxDate = today.toISOString().split('T')[0];
  const dateInputRef = useRef(null);
  const handleDateInputClick = () => {
    dateInputRef.current?.showPicker();
  };

  // const capitalizeFirstLetter = (str) => {
  //   if (!str) return "";
  //   return str
  //     .split(" ")
  //     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  //     .join(" ");
  // };



  return (
    <React.Fragment>
      <div className="epf-container">
        <Routes>
          <Route path="/registrationnavbar" element={<RegistrationNavbar />} />
        </Routes>
        <div className="epf-box">
          <h1 className="epf-heading">EPF Form</h1>
          <div className="pendingdetails-heading">
            <span className="highlight-label">Temporary ID :</span> {employeeUniqueId}-{personalData.employeeName} {personalData.surName}-{personalData.jobPosition?.designationName}
          </div>
          <form onSubmit={handleSubmit}>
            <h2 className="epf-heading">Personal Details</h2>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nameAsAadhar"
                    value={personalData.nameAsAadhar}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        const updatedValue = capitalizeFirstLetter(value);
                        handleInputChange({ target: { name: "nameAsAadhar", value: updatedValue } });
                      }
                    }}
                    placeholder=" "
                    // disabled
                    required
                  />
                  <label><span className="required">*</span>Employee Name</label>
                  {errors.nameAsAadhar && <p className="error">{errors.nameAsAadhar}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="guardianName"
                    value={uanNumber}
                    onChange={(e) => { setUanNumber(e.target.value); }}
                    placeholder=" "
                    required
                  />
                  <label><span className="required">*</span>PF / UAN Number</label>
                  {errors.guardianName && <p className="error">{errors.guardianName}</p>}
                </div>
              </div>

            </div>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.dateOfBirth || !!personalData.dateOfBirth ? 'float-label' : ''}`}>
                  <DatePicker
                    selected={personalData.dateOfBirth ? new Date(personalData.dateOfBirth) : null}
                    onChange={(date) => {
                      const formatted = date ? date.toISOString().split('T')[0] : '';
                      handleInputChange({ target: { name: 'dateOfBirth', value: formatted } });
                    }}
                    dateFormat="dd-MM-yyyy"
                    placeholderText=""
                    className="did-floating-input"
                    onFocus={() => setIsFocused(prev => ({ ...prev, dateOfBirth: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, dateOfBirth: false }))}
                    ref={dateInputRef}
                    minDate={new Date(minDate)}
                    maxDate={new Date(maxDate)}
                  //  disabled
                  />
                  <label><span className="required">*</span>Date of Birth</label>
                  {errors.dateOfBirth && <p className="error">{errors.dateOfBirth}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.gender || !!personalData.gender ? 'float-label' : ''}`}>
                  <Select
                    name="gender"
                    options={genders}
                    value={genders.find(opt => opt.value === personalData.gender) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'gender')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, gender: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, gender: false }))}
                    placeholder=" "
                    isClearable
                    //isDisabled
                    styles={{
                      menuPortal: base => ({
                        ...base,
                        zIndex: 9999,
                        position: 'absolute',
                        top: 'auto',
                        bottom: '100%'
                      }),
                      menu: base => ({
                        ...base,
                        marginTop: '5px',
                        zIndex: 9999
                      }),
                      control: (provided, state) => ({
                        ...provided,
                        padding: '1px',
                        backgroundColor: state.isDisabled ? '#e4e4e4' : 'white',
                        borderColor: state.isDisabled ? '#ccc' : provided.borderColor,
                        cursor: state.isDisabled ? 'not-allowed' : 'default',
                        opacity: 1,
                        pointerEvents: state.isDisabled ? 'auto' : 'auto'
                      })
                    }}
                  />
                  <label><span className="required">*</span>Gender</label>
                  {errors.gender && <p className="error">{errors.gender}</p>}
                </div>
              </div>

            </div>

            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="guardianName"
                    value={personalData.guardianName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder=" "
                    required
                  />
                  <label><span className="required">*</span>Father's / Spouse Name</label>
                  {errors.guardianName && <p className="error">{errors.guardianName}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.relationship || !!personalData.relationship ? 'float-label' : ''}`}>
                  <Select
                    name="relationship"
                    options={relationship}
                    value={relationship.find(opt => opt.value === personalData.relationship) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'relationship')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, relationship: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, relationship: false }))}
                    placeholder=" "
                    isClearable
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999, position: 'absolute', top: 'auto', bottom: '100%' }),
                      menu: base => ({ ...base, marginTop: '5px', zIndex: 9999 }),
                      control: provided => ({ ...provided, padding: '1px' }),
                      valueContainer: provided => ({ ...provided, padding: '3px 6px' }),
                    }}
                  />
                  <label><span className="required">*</span>Relationship</label>
                  {errors.relationship && <p className="error">{errors.relationship}</p>}
                </div>
              </div>


            </div>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="contactNumber"
                    value={personalData.contactNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    //disabled
                    required
                    placeholder=" "
                    maxLength="10"
                    pattern="^[0-9]{10}$"
                    title="Please enter a valid 10-digit phone number."
                  />
                  <label><span className="required">*</span>Mobile Number</label>
                  {errors.contactNumber && <p className="error">{errors.contactNumber}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.nationality || !!personalData.nationality ? 'float-label' : ''}`}>
                  <Select
                    name="nationality"
                    options={nationalities}
                    value={nationalities.find(opt => opt.value === personalData.nationality) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'nationality')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, nationality: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, nationality: false }))}
                    placeholder=" "
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                      control: provided => ({ ...provided, padding: '1px' }),
                      valueContainer: provided => ({ ...provided, padding: '3px 6px' }),
                    }}
                  />
                  <label><span className="required">*</span>Nationality</label>
                  {errors.nationality && <p className="error">{errors.nationality}</p>}
                </div>
              </div>

            </div>
            <div className="epf-maincontainer">



            </div>
            <div className="epf-maincontainer">

              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="guardianName"
                    value={personalData.emailAddress}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        handleInputChange(e);
                      }
                    }}
                    placeholder=" "
                    required
                  />
                  <label><span className="required">*</span>Email Id</label>
                  {errors.guardianName && <p className="error">{errors.guardianName}</p>}
                </div>
              </div>


              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isQualFocused || !!personalData.qualification ? 'float-label' : ''}`}>
                  <Select
                    name="qualification"
                    options={qualifications}
                    value={qualifications.find((q) => String(q.value) === String(personalData.qualification)) || null}
                    onChange={(selected) => {
                      handleInputChange(selected, "qualification");
                    }}
                    onFocus={() => setIsQualFocused(true)}
                    onBlur={() => setIsQualFocused(false)}
                    placeholder=""
                    isClearable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: "#fff",
                        borderColor: "#c9c9c9",
                      }),
                      singleValue: (provided) => ({ ...provided, color: "#474747" }),
                    }}
                  />
                  <label>
                    <span className="required">*</span>Qualification
                  </label>
                  {errors.qualification && <p className="error">{errors.qualification}</p>}
                </div>
              </div>
            </div>

            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.maritalStatus || !!personalData.maritalStatus ? 'float-label' : ''}`}>
                  <Select
                    name="maritalStatus"
                    options={maritalStatuses}
                    value={maritalStatuses.find(opt => opt.value === personalData.maritalStatus) || null}
                    onChange={(selectedOption) => handleInputChange(selectedOption, 'maritalStatus')}
                    onFocus={() => setIsFocused(prev => ({ ...prev, maritalStatus: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, maritalStatus: false }))}
                    placeholder=" "
                    isClearable
                    styles={{
                      menuPortal: base => ({ ...base, zIndex: 9999, position: 'absolute', top: 'auto', bottom: '100%' }),
                      menu: base => ({ ...base, marginTop: '5px', zIndex: 9999 }),
                      control: provided => ({ ...provided, padding: '1px' }),
                      valueContainer: provided => ({ ...provided, padding: '3px 6px' }),
                    }}
                  />
                  <label><span className="required">*</span>Marital Status</label>
                  {errors.maritalStatus && <p className="error">{errors.maritalStatus}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-input-container-radio">
                  <label><span className="required">*</span>Physically Handicapped:</label>
                  <label>
                    <input
                      type="radio"
                      name="physicallyHandicapped"
                      value="true"
                      checked={personalData.physicallyHandicapped === "true" || personalData.physicallyHandicapped === true}
                      onChange={handleInputChange}
                    />&nbsp; Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="physicallyHandicapped"
                      value="false"
                      checked={personalData.physicallyHandicapped === "false" || personalData.physicallyHandicapped === false}
                      onChange={handleInputChange}
                    />&nbsp; No
                  </label>
                  {errors.physicallyHandicapped && <p className="error">{errors.physicallyHandicapped}</p>}
                </div>
              </div>

            </div>

            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-input-container-radio">
                  <label><span className="required">*</span>Visual Problem:</label>
                  <label>
                    <input
                      type="radio"
                      name="visualProblem"
                      value="true"
                      checked={personalData.visualProblem === "true" || personalData.visualProblem === true}
                      onChange={handleInputChange}
                    />&nbsp; Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="visualProblem"
                      value="false"
                      checked={personalData.visualProblem === "false" || personalData.visualProblem === false}
                      onChange={handleInputChange}
                    />&nbsp; No
                  </label>
                  {errors.visualProblem && <p className="error">{errors.visualProblem}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-input-container-radio">
                  <label><span className="required">*</span>Hearing Problem:</label>
                  <label>
                    <input
                      type="radio"
                      name="hearingProblem"
                      value="true"
                      checked={personalData.hearingProblem === "true" || personalData.hearingProblem === true}
                      onChange={handleInputChange}
                    />&nbsp; Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="hearingProblem"
                      value="false"
                      checked={personalData.hearingProblem === "false" || personalData.hearingProblem === false}
                      onChange={handleInputChange}
                    />&nbsp; No
                  </label>
                  {errors.hearingProblem && <p className="error">{errors.hearingProblem}</p>}
                </div>
              </div>
            </div>
             <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-input-container-radio">
                  <label><span className="required">*</span>Locomotive:</label>
                  <label>
                    <input
                      type="radio"
                      name="locomotive"
                      value="true"
                      checked={personalData.locomotive === "true" || personalData.locomotive === true}
                      onChange={handleInputChange}
                    />&nbsp; Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="locomotive"
                      value="false"
                      checked={personalData.locomotive === "false" || personalData.locomotive === false}
                      onChange={handleInputChange}
                    />&nbsp; No
                  </label>
                  {errors.locomotive && <p className="error">{errors.locomotive}</p>}
                </div>
              </div>
             
            </div>
            <h2 className="epf-heading">Bank Details</h2>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="accountHolderName"
                    value={bankData.accountHolderName}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(value)) {
                        value = formatAccountHolderName(value);
                        handleChange({ target: { name: "accountHolderName", value } });
                      }
                    }}
                    required
                    placeholder=" "
                  // disabled
                  />
                  <label><span className="required">*</span>Name as per Bank Account</label>
                  {errors.accountHolderName && <p className="error">{errors.accountHolderName}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="accountNumber"
                    value={bankData.accountNumber}
                    onChange={(e) => {
                      let value = e.target.value;
                      if (/^\d{0,15}$/.test(value)) {
                        handleAccountChange({ target: { name: "accountNumber", value } });
                      } else {
                        setErrors(prev => ({
                          ...prev,
                          accountNumber: "Only numbers allowed, max 15 digits"
                        }));
                      }
                    }}
                    maxLength={15}
                    required
                    placeholder=" "
                  // disabled
                  />
                  <label><span className="required">*</span>Account Number</label>
                  {errors.accountNumber && <p className="error">{errors.accountNumber}</p>}
                </div>
              </div>
            </div>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="ifscCode"
                    placeholder=""
                    value={bankData.ifscCode}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      handleChange({ target: { name: "ifscCode", value } });
                      let error = '';
                      if (value && value.length === 11 && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value)) {
                        error = '4 letters (bank) + 0 + 6 alphanumeric (branch)';
                      }
                      setErrors(prev => ({ ...prev, ifscCode: error }));
                    }}
                    maxLength={11}
                    pattern="^[A-Z]{4}0[A-Z0-9]{6}$"
                    title="The IFSC code is an 11-character code: The first 4 characters are uppercase letters (bank code), followed by a '0', and the last 6 characters can be alphanumeric (branch code)."
                    style={{ borderColor: errors.ifscCode ? 'red' : undefined }}
                  //disabled
                  />
                  <label><span className="required">*</span>IFSC Code</label>
                  {errors.ifscCode && <p className="error">{errors.ifscCode}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="branchName"
                    value={bankData.branchName}
                    onChange={(e) => {
                      const formatted = formatBranchName(e.target.value);
                      handleChange({ target: { name: "branchName", value: formatted } });
                    }}
                    placeholder=""
                  // disabled
                  />
                  <label>Branch Name</label>
                  {errors.branchName && <p className="error">{errors.branchName}</p>}
                </div>
              </div>
            </div>
            <h2 className="epf-heading">Govt ID Details</h2>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeName"
                    value={personalData.nameAsAadhar}
                    onChange={(e) => {
                      const formatted = capitalizeFirstLetter(e.target.value);
                      handleInputChange({ target: { name: "nomineeName", value: formatted } });
                    }}
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Name as per Aadhaar</label>
                  {errors.nameAsAadhar && <p className="error">{errors.nameAsAadhar}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeName"
                    value={personalData.aadhaarNumber}
                    onChange={(e) => {
                      const formatted = capitalizeFirstLetter(e.target.value);
                      handleInputChange({ target: { name: "nomineeName", value: formatted } });
                    }}
                    placeholder=" "
                  />
                  <label><span className="required">*</span>Aadhaar Number</label>
                  {errors.aadhaarNumber && <p className="error">{errors.aadhaarNumber}</p>}
                </div>
              </div>
            </div>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeAddress"
                    value={personalData.nameAsPerPanCard}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Name as per PAN</label>
                  {errors.nameAsPerPanCard && <p className="error">{errors.nameAsPerPanCard}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeAddress"
                    value={personalData.panCardNumber}
                    onChange={handleInputChange}
                    placeholder=" "
                  />
                  <label>Pan Number</label>
                  {errors.panCardNumber && <p className="error">{errors.panCardNumber}</p>}
                </div>
              </div>
            </div>
            <h2 className="epf-heading">Onboarding Details</h2>
            <div className="epf-maincontainer">
              <div className="epf-container-part">
                <div className={`epf-floating-label-container-2 ${isFocused.onBoardingDate || !!employeeCreationData.onBoardingDate ? 'float-label' : ''}`}>
                  <DatePicker
                    selected={employeeCreationData.onBoardingDate ? new Date(employeeCreationData.onBoardingDate) : null}
                    onChange={(date) => {
                      if (date) {
                        const localDate = new Date(date);
                        localDate.setHours(0, 0, 0, 0);
                        const formatted = localDate.toLocaleDateString('en-CA');
                        handleInputChange({ target: { name: 'onBoardingDate', value: formatted } });
                      } else {
                        handleInputChange({ target: { name: 'onBoardingDate', value: '' } });
                      }
                    }}
                    dateFormat="dd-MM-yyyy"
                    placeholderText=""
                    className="did-floating-input"
                    onFocus={() => setIsFocused(prev => ({ ...prev, onBoardingDate: true }))}
                    onBlur={() => setIsFocused(prev => ({ ...prev, onBoardingDate: false }))}
                    ref={dateInputRef}
                    minDate={new Date(minDate)}
                    maxDate={new Date(maxDate)}
                  />
                  <label><span className="required">*</span>Onboarding Date</label>
                  {errors.onBoardingDate && <p className="error">{errors.onBoardingDate}</p>}
                </div>
              </div>
              <div className="epf-container-part">
                <div className="epf-floating-label-container-1">
                  <input
                    type="text"
                    name="nomineeName"
                    value={personalData.designation}
                    onChange={(e) => {
                      const formatted = capitalizeFirstLetter(e.target.value);
                      handleInputChange({ target: { name: "nomineeName", value: formatted } });
                    }}
                    placeholder=" "
                  />
                  <label>Designation</label>
                  {errors.designation && <p className="error">{errors.designation}</p>}
                </div>
              </div>
            </div>

            <div className="epf-btn-div">
              <button type="submit" disabled={!isFormValid} className="epf-btn">
                Submit EPF Form
              </button>
              <button type="submit" disabled={!isFormValid} className="epf-btn-can">
                cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EPFForm